/**
 * PixelLabClient — Browser-side client for the PixelLab v2 API.
 *
 * Handles async job submission, polling, and image download.
 * User supplies their own API key (stored in localStorage).
 *
 * API: https://api.pixellab.ai/v2/
 * Auth: Bearer token from https://pixellab.ai/account
 */

const API_BASE = 'https://api.pixellab.ai/v2';
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_TIME_MS = 300000; // 5 minutes

export class PixelLabClient {
  constructor(apiKey) {
    this._apiKey = apiKey;
  }

  _headers() {
    return {
      'Authorization': `Bearer ${this._apiKey}`,
      'Content-Type': 'application/json',
    };
  }

  async _post(path, body) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: this._headers(),
      body: JSON.stringify(body),
    });
    if (res.status === 402) throw new Error('Insufficient PixelLab credits. Top up at pixellab.ai/account.');
    if (res.status === 429) throw new Error('Too many concurrent jobs. Wait for current jobs to finish.');
    if (res.status === 401 || res.status === 403) throw new Error('Invalid API key. Check your key at pixellab.ai/account.');
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`PixelLab API error (${res.status}): ${text || 'Unknown error'}`);
    }
    return res.json();
  }

  async _get(path) {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: this._headers(),
    });
    if (!res.ok) throw new Error(`PixelLab API error (${res.status})`);
    return res.json();
  }

  /** Check account balance/credits. */
  async getBalance() {
    return this._get('/balance');
  }

  /**
   * Create a top-down Wang tileset (16 or 23 tiles).
   * @returns {Promise<Object>} job result with tileset data
   */
  async createTopdownTileset({ lowerDesc, upperDesc, transitionDesc, tileSize = 16, onProgress }) {
    const body = {
      lower_description: lowerDesc,
      upper_description: upperDesc,
      tile_size: { width: tileSize, height: tileSize },
    };
    if (transitionDesc) {
      body.transition_description = transitionDesc;
      body.transition_size = 0.5;
    }

    onProgress?.('Submitting tileset job...');
    const result = await this._post('/generate-topdown-tileset', body);

    // API returns 202 with job_id for async, or 200 with data for sync
    if (result.data?.job_id) {
      return this._pollJob(result.data.job_id, onProgress);
    }
    return result.data;
  }

  /**
   * Create individual tile variations.
   * @returns {Promise<Object>} job result with tile images
   */
  async createTilesPro({ description, tileSize = 32, tileType = 'square_topdown', nTiles = 6, onProgress }) {
    const body = {
      description,
      tile_size: tileSize,
      tile_type: tileType,
      tile_view: 'high top-down',
      n_tiles: nTiles,
    };

    onProgress?.('Submitting tile generation job...');
    const result = await this._post('/generate-tiles-pro', body);

    if (result.data?.job_id) {
      return this._pollJob(result.data.job_id, onProgress);
    }
    return result.data;
  }

  /**
   * Poll a background job until completion.
   * @returns {Promise<Object>} completed job data
   */
  async _pollJob(jobId, onProgress) {
    const startTime = Date.now();
    let pollCount = 0;

    while (Date.now() - startTime < MAX_POLL_TIME_MS) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      pollCount++;

      const result = await this._get(`/background-jobs/${jobId}`);
      const status = result.data?.status || result.status;

      if (status === 'completed') {
        onProgress?.('Generation complete! Loading image...');
        return result.data;
      }

      if (status === 'failed') {
        throw new Error(result.data?.error || 'Generation failed. Try a different description.');
      }

      const eta = result.data?.eta_seconds;
      const etaText = eta ? ` (~${Math.ceil(eta)}s remaining)` : '';
      onProgress?.(`Generating${etaText}... (poll ${pollCount})`);
    }

    throw new Error('Generation timed out after 5 minutes. The job may still be processing — check pixellab.ai.');
  }

  /**
   * Download an image from a PixelLab URL and return as Blob.
   * Handles redirects (PixelLab uses 302 → Backblaze).
   */
  async downloadImage(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to download image (${res.status})`);
    return res.blob();
  }
}
